import React, { useMemo } from 'react';
import { Tooltip as AntTooltip } from 'antd';
import type { TooltipProps as AntTooltipProps } from 'antd';
import { colors } from '../../theme/colors';
import { cn } from '@/lib/utils';

export type TooltipPlacement =
  | 'top'
  | 'topLeft'
  | 'topRight'
  | 'bottom'
  | 'bottomLeft'
  | 'bottomRight'
  | 'left'
  | 'leftTop'
  | 'leftBottom'
  | 'right'
  | 'rightTop'
  | 'rightBottom';

export type TooltipTrigger = 'hover' | 'focus' | 'click' | 'contextMenu';

export type TooltipVariant = 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info' | 'light';

export type TooltipSize = 'small' | 'medium' | 'large';

export interface TooltipProps extends Omit<AntTooltipProps, 'color' | 'overlayStyle' | 'overlayClassName'> {
  /** Content to display in tooltip */
  title: React.ReactNode;
  /** Element that triggers the tooltip */
  children: React.ReactElement;
  /** Tooltip placement relative to trigger */
  placement?: TooltipPlacement;
  /** How tooltip is triggered */
  trigger?: TooltipTrigger | TooltipTrigger[];
  /** Color variant of tooltip */
  variant?: TooltipVariant;
  /** Size of tooltip */
  size?: TooltipSize;
  /** Whether to show arrow */
  arrow?: boolean;
  /** Maximum width of tooltip */
  maxWidth?: number | string;
  /** Whether tooltip is disabled */
  disabled?: boolean;
  /** Delay before showing tooltip (ms) */
  showDelay?: number;
  /** Delay before hiding tooltip (ms) */
  hideDelay?: number;
  /** Custom class for tooltip overlay */
  className?: string;
  /** Whether tooltip should be responsive (auto-adjust on mobile) */
  responsive?: boolean;
  /** Z-index of tooltip */
  zIndex?: number;
  /** Callback when visibility changes */
  onVisibleChange?: (visible: boolean) => void;
  /** Controlled visibility */
  open?: boolean;
  /** Offset from trigger element [horizontal, vertical] */
  offset?: [number, number];
  /** Whether the tooltip wrapper should be block-level (full width) */
  block?: boolean;
}

// Variant color configurations
const variantColors: Record<TooltipVariant, { bg: string; text: string; border?: string }> = {
  default: {
    bg: colors.textInverse,
    text: colors.white,
  },
  primary: {
    bg: colors.primary,
    text: colors.white,
  },
  success: {
    bg: colors.success,
    text: colors.white,
  },
  warning: {
    bg: colors.warning,
    text: colors.textInverse,
  },
  error: {
    bg: colors.error,
    text: colors.white,
  },
  info: {
    bg: colors.info,
    text: colors.white,
  },
  light: {
    bg: colors.white,
    text: colors.textDefault,
    border: colors.surfaceSubtle,
  },
};

// Size configurations
const sizeConfig: Record<TooltipSize, { padding: string; fontSize: string; maxWidth: number }> = {
  small: {
    padding: '4px 8px',
    fontSize: '12px',
    maxWidth: 200,
  },
  medium: {
    padding: '8px 12px',
    fontSize: '14px',
    maxWidth: 300,
  },
  large: {
    padding: '12px 16px',
    fontSize: '14px',
    maxWidth: 400,
  },
};

// Responsive placement mapping for mobile
const responsivePlacementMap: Record<TooltipPlacement, TooltipPlacement> = {
  top: 'top',
  topLeft: 'top',
  topRight: 'top',
  bottom: 'bottom',
  bottomLeft: 'bottom',
  bottomRight: 'bottom',
  left: 'top',
  leftTop: 'top',
  leftBottom: 'bottom',
  right: 'top',
  rightTop: 'top',
  rightBottom: 'bottom',
};

export const Tooltip: React.FC<TooltipProps> = ({
  title,
  children,
  placement = 'top',
  trigger = 'hover',
  variant = 'default',
  size = 'medium',
  arrow = true,
  maxWidth,
  disabled = false,
  showDelay = 100,
  hideDelay = 100,
  className,
  responsive = true,
  zIndex = 1070,
  onVisibleChange,
  open,
  offset,
  block = false,
  ...props
}) => {
  // Detect if on mobile device
  const isMobile = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768;
  }, []);

  // Get responsive placement
  const effectivePlacement = useMemo(() => {
    if (responsive && isMobile) {
      return responsivePlacementMap[placement];
    }
    return placement;
  }, [placement, responsive, isMobile]);

  // Get variant colors
  const colorConfig = variantColors[variant];
  const sizeSettings = sizeConfig[size];

  // Calculate effective max width
  const effectiveMaxWidth = useMemo(() => {
    if (maxWidth) {
      return typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth;
    }
    // Responsive max width
    if (responsive && isMobile) {
      return `${Math.min(sizeSettings.maxWidth, window.innerWidth - 32)}px`;
    }
    return `${sizeSettings.maxWidth}px`;
  }, [maxWidth, responsive, isMobile, sizeSettings.maxWidth]);

  // Build tooltip body styles
  const tooltipBodyStyle: React.CSSProperties = useMemo(() => ({
    backgroundColor: colorConfig.bg,
    color: colorConfig.text,
    padding: sizeSettings.padding,
    fontSize: sizeSettings.fontSize,
    maxWidth: effectiveMaxWidth,
    borderRadius: '6px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    border: colorConfig.border ? `1px solid ${colorConfig.border}` : 'none',
    wordWrap: 'break-word',
    lineHeight: 1.5,
  }), [colorConfig, sizeSettings, effectiveMaxWidth]);

  // Build align configuration for proper positioning
  const alignConfig = useMemo(() => {
    if (offset) {
      return { offset };
    }
    // Default offset for better visual alignment
    return { offset: [0, 4] as [number, number] };
  }, [offset]);

  // Don't render tooltip if disabled
  if (disabled) {
    return children;
  }

  // Don't render if no title
  if (!title) {
    return children;
  }

  // Wrap the child so Ant Tooltip can attach a ref without findDOMNode warnings.
  // Block mode is shrink-safe for truncated text inside constrained layouts.
  const childWrapperStyle: React.CSSProperties = {
    display: block ? 'block' : 'inline-flex',
    width: block ? '100%' : undefined,
    maxWidth: block ? '100%' : undefined,
    minWidth: 0,
  };

  const childElement = React.isValidElement(children) ? (
    <span style={childWrapperStyle}>{children}</span>
  ) : (
    <span style={childWrapperStyle}>{children}</span>
  );

  return (
    <AntTooltip
      title={title}
      placement={effectivePlacement}
      trigger={trigger}
      arrow={arrow ? { pointAtCenter: true } : false}
      mouseEnterDelay={showDelay / 1000}
      mouseLeaveDelay={hideDelay / 1000}
      align={alignConfig}
      classNames={{
        root: cn(
          'nf-tooltip',
          `nf-tooltip-${variant}`,
          `nf-tooltip-${size}`,
          className
        ),
      }}
      styles={{ body: tooltipBodyStyle }}
      zIndex={zIndex}
      onOpenChange={onVisibleChange}
      open={open}
      destroyOnHidden
      fresh
      {...props}
    >
      {childElement}
    </AntTooltip>
  );
};

// Compound components for common use cases
export const InfoTooltip: React.FC<Omit<TooltipProps, 'variant'>> = (props) => (
  <Tooltip variant="info" {...props} />
);

export const SuccessTooltip: React.FC<Omit<TooltipProps, 'variant'>> = (props) => (
  <Tooltip variant="success" {...props} />
);

export const WarningTooltip: React.FC<Omit<TooltipProps, 'variant'>> = (props) => (
  <Tooltip variant="warning" {...props} />
);

export const ErrorTooltip: React.FC<Omit<TooltipProps, 'variant'>> = (props) => (
  <Tooltip variant="error" {...props} />
);

export default Tooltip;
